import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const compareContacts = (a, b) => a.id === b.id;

export const clientContactSlice = createSlice({
    name: 'clientContacts',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            action.payload.ids.forEach(id => {
                state.data[id] = action.payload.data.filter(c => c.clientid === id);
            })
        },
        addOrUpdate: (state, action) => {
            const contacts = ensureArray(action.payload);
            contacts.forEach(c => {
                if (!state.data[c.clientid]) {
                    state.data[c.clientid] = [c];
                } else {
                    let idx = state.data[c.clientid].findIndex(contact => compareContacts(c, contact));
                    if (idx >= 0) {
                        state.data[c.clientid].splice(idx, 1, c);
                    } else {
                        state.data[c.clientid] = [...state.data[c.clientid], c];
                    }
                }
            })
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byClient = groupBy(toRemove, 'clientid');
            Object.entries(byClient).forEach(([clientid, contacts]) => {
                const ids = new Set(contacts.map(c => c.id));
                state.data[clientid] = state.data[clientid].filter(c => !ids.has(c.id));
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => clientContactSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'contact_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'contact_one');

const fetchClientContacts = clientid => network => network.get('/api/v1/clientcontacts/' + encodeURIComponent(clientid), data => clientContactSlice.actions.set({ data, ids: [clientid] }), visualizeFailure('get'));
export const createClientContact = (network, data) => network.post('/api/v1/clientcontact', data, clientContactSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateClientContact = (network, data, propagate) => network.put('/api/v1/clientcontact?updateEvents=' + (propagate ? 1 : 0), data, clientContactSlice.addOrUpdate, visualizeFailure('update'));
export const removeClientContact = (network, id) => network.delete('/api/v1/clientcontact/' + window.encodeURIComponent(id), clientContactSlice.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectAllClientContactData = state => state.clientContacts.data;
const selectClientContacts = clientid => createSelector([selectAllClientContactData], contacts => contacts[clientid] ?? []);
// #endregion

const listen = {
    clientContactCreated: (contact, { dispatch, websocket }) => {
        markFetched(websocket, 'contact', contact.id);
        dispatch(clientContactSlice.actions.addOrUpdate(contact))
    },
    clientContactUpdated: (contact, { dispatch }) => dispatch(clientContactSlice.actions.addOrUpdate(contact)),
    clientContactRemoved: (contact, { dispatch }) => dispatch(clientContactSlice.actions.remove(contact)),
}

const clientInitContactsByID = initMapFactory(fetchClientContacts, listen);
registerEntityBasedFetch('client', clientInitContactsByID);
const cleanup = { root: clientContactSlice.name, all: state => Object.values(selectAllClientContactData(state)).flat(), removeAll: clientContactSlice.actions.remove, key: contact => contact.id };
export const [useClientContacts] = dataHookFactory(selectClientContacts, clientInitContactsByID, { cleanup });

const reducer = { name: clientContactSlice.name, reducer: clientContactSlice.reducer }
export default reducer;